/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Builds the c2pa-rs-style `signature_info` for a manifest from its COSE_Sign1
 * and signing certificate. Note c2pa-rs reports the signer (certificate
 * *subject*) under `issuer`/`common_name`, and the signing `time` from the
 * embedded RFC 3161 timestamp (`sigTst2` / `sigTst`).
 */

import type { SignatureInfo } from '../types'
import { algSpec } from './alg'
import { coseAlg, type CoseSign1 } from './decodeSign1'
import type { CertInfo } from '../x509/parseCert'
import { extractTstGenTime } from '../x509/parseTimestamp'

/** Reads the first RFC 3161 token from a `sigTst`/`sigTst2` header value. */
function firstTstToken (sign1: CoseSign1): Uint8Array | null {
    const v = sign1.unprotectedHeader.get('sigTst2') ?? sign1.unprotectedHeader.get('sigTst')
    const tokens = (v as { tstTokens?: Array<{ val?: unknown }> } | undefined)?.tstTokens
    const val = tokens?.[0]?.val
    return val instanceof Uint8Array ? val : null
}

/** The signing time (TSTInfo genTime) from the COSE timestamp, or null. */
export function signingTime (sign1: CoseSign1): string | null {
    const token = firstTstToken(sign1)
    return token ? extractTstGenTime(token) : null
}

export function buildSignatureInfo (sign1: CoseSign1, cert: CertInfo): SignatureInfo {
    const algId = sign1 ? coseAlg(sign1) : undefined
    const alg = algId !== undefined ? algSpec(algId)?.signingAlg : undefined
    const info: SignatureInfo = {
        alg: alg ?? null,
        // c2pa-rs reports the signer (subject): organization as `issuer`, common name
        // as `common_name`. It uses the organization only — no CN fallback (a cert
        // with a CN but no O reports issuer:null).
        issuer: cert.subject.O ?? null,
        common_name: cert.subject.CN ?? null,
        cert_serial_number: cert.serialNumber
    }
    const time = signingTime(sign1)
    if (time) info.time = time
    return info
}
